import clsx from "clsx";
import { ReactNode, useEffect, useRef, useState } from "react";
import { AngleDown } from "../icons/Icons";

export const CustomSelect = <K extends string>({
  value,
  options,
  onChange,
  disabled,
  className,
  placeholder = "...",
  ariaLabel,
}: {
  value?: K;
  options: readonly { key: K; label: ReactNode }[];
  onChange: (key: K) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
}) => {
  const node = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const ref = node.current;
      if (ref && e.target instanceof Node && ref.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const selected = options.find((o) => o.key === value);
  const isDisabled = disabled || options.length === 0;

  return (
    <div
      ref={node}
      className={clsx({
        "custom-select condensed": true,
        "custom-select--open": open,
        "custom-select--disabled": isDisabled,
        [`${className}`]: className,
      })}>
      <div
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        tabIndex={isDisabled ? -1 : 0}
        className="custom-select__selected"
        onClick={() => !isDisabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (isDisabled) return;
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}>
        <span className="custom-select__label">
          {selected ? selected.label : placeholder}
        </span>
        <AngleDown />
      </div>

      {open && (
        <div className="custom-select__dropdown" role="listbox">
          {options.map((o) => (
            <a
              key={o.key}
              role="option"
              aria-selected={o.key === value}
              className={clsx({
                "custom-select__item": true,
                "custom-select__item--selected": o.key === value,
              })}
              onClick={() => {
                setOpen(false);
                onChange(o.key);
              }}>
              {o.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
